<template>
  <div class="sucursales-wrapper">
    <ModalLoading :isLoading="isLoading" />

    <div class="opciones">
      <button
        class="btn-agregar"
        @click="openModal"
      >
        Agregar sucursales
      </button>
      <ExportButton
        :columns="columns"
        :rows="rows"
        fileName="Sucursales.pdf"
        class="export-button"
      />
      <div class="search-bar">
        <input
          class="busqueda"
          type="text"
          v-model="searchQuery"
          placeholder="Buscar sucursal..."
        />
      </div>
    </div>

    <div class="table-container">
      <!-- Indicador de carga -->
      <div v-if="isLoading" class="loading-indicator">
        Cargando sucursales...
      </div>

      <!-- Mensaje si no hay datos -->
      <div v-else-if="paginatedSucursales.length === 0" class="no-data">
        No se encontraron sucursales para mostrar.
      </div>

      <table v-else class="table">
        <thead>
          <tr>
            <th class="col-id">#</th>
            <th class="col-nombre">Nombre</th>
            <th class="col-correo">Correo</th>
            <th class="col-telefono">Telefono</th>
            <th class="col-direccion">Direccion</th>
            <th class="col-acciones">Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(sucursal, index) in paginatedSucursales" :key="index">
            <td>{{ (currentPage - 1) * pageSize + index + 1 }}</td>
            <td>{{ sucursal.nombre_administrativo }}</td>
            <td>{{ sucursal.correo }}</td>
            <td>{{ sucursal.telefono }}</td>
            <td>{{ sucursal.direccion }}</td>
            <td>
              <button
                class="accion-btn accion-editar"
                @click="editSucursal(sucursal)"
                title="Editar sucursal"
              >
                <i class="bi bi-pencil-fill"></i>
              </button>
              <button
                class="accion-btn accion-descargar"
                @click="descargarDatosSucursal(sucursal)"
                :disabled="descargandoSucursal === sucursal.id_sucursal"
                :title="descargandoSucursal === sucursal.id_sucursal ? 'Generando ZIP…' : 'Descargar datos'"
              >
                <i :class="descargandoSucursal === sucursal.id_sucursal ? 'bi bi-arrow-repeat spin' : 'bi bi-download'"></i>
              </button>
              <button
                class="accion-btn accion-eliminar"
                @click="deleteSucursal(sucursal)"
                title="Eliminar sucursal"
              >
                <i class="bi bi-x-lg"></i>
              </button>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Paginación -->
      <div class="pagination-wrapper">
        <div class="pagination-info">
          Mostrando {{ (currentPage - 1) * pageSize + 1 }} a
          {{ Math.min(currentPage * pageSize, filteredSucursales.length) }} de
          {{ filteredSucursales.length }} registros
        </div>
        <div class="pagination-container">
          <button
            class="pagination-button"
            :disabled="currentPage === 1"
            @click="previousPage"
          >
            Anterior
          </button>

          <button
            class="pagination-button"
            :disabled="currentPage === totalPages"
            @click="nextPage"
          >
            Siguiente
          </button>
        </div>
      </div>
    </div>

    <!-- Modal para agregar o editar Sucursales -->
    <div v-if="isModalOpen" class="modal">
      <div class="modal-sucursales">
        <div class="modal-header">
          <h2 class="h2-modal-content">
            {{ isEditing ? "Editar Sucursal" : "Agregar Sucursal" }}
          </h2>
        </div>

        <div class="modal-body">
          <div class="form-group">
            <label>Nombre:</label>
            <input
              ref="nombreInput"
              v-model="sucursalForm.nombre_administrativo"
              type="text"
              required
            />
          </div>

          <div class="form-group">
            <label>Correo:</label>
            <input v-model="sucursalForm.correo" type="text" required />
          </div>

          <div class="form-group">
            <label>Teléfono:</label>
            <div class="phone-input-container">
              <select
                v-model="selectedCountry"
                @change="updatePhoneValidation"
                class="select-phone"
              >
                <option value="">País</option>
                <option
                  v-for="(country, code) in countryData"
                  :key="code"
                  :value="code"
                >
                  {{ country.emoji }} {{ country.code }}
                </option>
              </select>
              <input
                v-model="sucursalForm.telefono"
                type="text"
                class="input-phone"
                :placeholder="'Número (' + phoneLength + ' dígitos)'"
                required
              />
            </div>
          </div>

          <div class="form-group">
            <label>Dirección:</label>
            <input v-model="sucursalForm.direccion" type="text" required />
          </div>
        </div>

        <div class="modal-footer">
          <btnGuardarModal
            :texto="isEditing ? 'Guardar Cambios' : 'Agregar Sucursal'"
            @click="guardarSucursal"
          >
          </btnGuardarModal>
          <btnCerrarModal
            :texto="'Cerrar'"
            @click="closeModal"
          ></btnCerrarModal>
        </div>
      </div>
    </div>

    <!-- Modal de confirmación -->
    <div class="modal" v-if="showConfirmModal">
      <div class="modal-confirm">
        <div class="modal-header">
          <h2>Confirmación</h2>
        </div>
        <div class="modal-body">
          <p>¿Estás seguro de que quieres eliminar esta sucursal?</p>
        </div>
        <div class="modal-footer">
          <div class="action-buttons">
            <btnGuardarModal
              texto="Sí, eliminar"
              style="background-color: red"
              @click="deleteSucursal"
            />
            <btnCerrarModal texto="No, regresar" @click="cancelDelete" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ExportButton from "../components/ExportButton.vue";
import ModalLoading from "@/components/ModalLoading.vue";
import btnGuardarModal from "../components/botones/modales/btnGuardar.vue";
import btnCerrarModal from "../components/botones/modales/btnCerrar.vue";
import { notis } from "../../services/notificaciones.js";
import { COUNTRY_CODES } from "../../services/countrySelector.js";
import { validacionesSucursal } from "../../services/validarCampos.js";
import solicitudes from "../../services/solicitudes.js";
import { setPageTitle } from "@/components/pageMetadata";
const { getSocket } = require('@/services/socket');

export default {
  components: {
    ExportButton,
    btnGuardarModal,
    btnCerrarModal,
    ModalLoading,
  },
  data() {
    return {
      titulo: "Administración de Sucursales",
      id_usuario: 1,
      id_empresa: 11,
      searchQuery: "",
      isModalOpen: false,
      isEditing: false,
      isLoading: false,
      editIndex: null,
      selectedCountry: "HN",
      countryData: COUNTRY_CODES,
      phoneLength: 8,
      currentPage: 1,
      pageSize: 10,
      showConfirmModal: false,
      sucursalToDelete: null,

      sucursalForm: {
        id_sucursal: 0,
        nombre_administrativo: "",
        correo: "",
        telefono: "",
        direccion: "",
      },
      sucursales: [],
      descargandoSucursal: null,
      columns: [
        { header: "#", dataKey: "index" },
        { header: "Nombre", dataKey: "nombre" },
        { header: "Correo", dataKey: "correo" },
        { header: "Direccion", dataKey: "direccion" },
        { header: "Teléfono", dataKey: "telefono" },
      ],
      rows: [],
    };
  },
  computed: {
    filteredSucursales() {
      return this.sucursales.filter(
        (sucursal) =>
          sucursal.nombre_administrativo
            .toLowerCase()
            .includes(this.searchQuery.toLowerCase()) ||
          sucursal.direccion
            .toLowerCase()
            .includes(this.searchQuery.toLowerCase())
      );
    },
    paginatedSucursales() {
      const startIndex = (this.currentPage - 1) * this.pageSize;
      const endIndex = startIndex + this.pageSize;
      return this.filteredSucursales.slice(startIndex, endIndex);
    },
    totalPages() {
      return Math.ceil(this.filteredSucursales.length / this.pageSize);
    },
  },
  methods: {
    updatePhoneValidation() {
      if (this.selectedCountry && this.countryData[this.selectedCountry]) {
        this.phoneLength = this.countryData[this.selectedCountry].length;
      }
    },

    closeModal() {
      this.isModalOpen = false;
      this.isEditing = false;
      this.selectedCountry = "HN";
      this.sucursalForm = {
        id_sucursal: 0,
        nombre_administrativo: "",
        correo: "",
        telefono: "",
        direccion: "",
      };
    },

    openModal() {
      this.isModalOpen = true;
      this.$nextTick(() => {
        this.$refs.nombreInput?.focus();
      });
    },

    async guardarSucursal() {
      this.isLoading = true;
      try {
        if (
          !validacionesSucursal.validarCampos(
            this.sucursalForm,
            this.selectedCountry
          )
        ) {
          return;
        }

        let response;
        let parametros;

        if (this.isEditing) {
          parametros = `/sucursales/actualizar-sucursal/${
            this.sucursales[this.editIndex].id_sucursal
          }`;
          response = await solicitudes.patchRegistroAuth(
            parametros,
            this.sucursalForm
          );

          if (response === true) {
            Object.assign(this.sucursales[this.editIndex], this.sucursalForm);
            notis("success", "Actualizando datos de sucursal...");
          } else {
            throw new Error(
              response.message || "Error al actualizar la sucursal"
            );
          }
        } else {
          // Verificar límite de sucursales del plan
          try {
            const plan = JSON.parse(localStorage.getItem('empresa_plan') || '{}');
            const maxS = plan.max_sucursales;
            if (maxS && this.sucursales.length >= maxS) {
              notis('error', `Límite alcanzado: máximo ${maxS} sucursal(es) en tu plan.`);
              this.isLoading = false;
              return;
            }
          } catch { /* plan check — ignore */ }

          parametros = `/sucursales/crear-sucursal/${this.id_usuario}`;
          response = await solicitudes.postRegistroAuth(
            parametros,
            this.sucursalForm
          );

          if (response && response.id_sucursal) {
            this.sucursales.push(response);
            notis("success", "Sucursal creada correctamente");
          } else {
            throw new Error("Error al crear la sucursal");
          }
        }

        this.closeModal();
        this.generateRows();
      } catch (error) {
        this.isLoading = false;
        notis("error", "Error");
      } finally {
        this.isLoading = false;
      }
    },

    editSucursal(sucursal) {
      this.isModalOpen = true;
      this.isEditing = true;
      this.editIndex = this.sucursales.findIndex(
        (item) => item.id_sucursal === sucursal.id_sucursal
      );
      this.sucursalForm = { ...sucursal };
      this.$nextTick(() => {
        this.$refs.nombreInput?.focus();
      });
    },

    cancelDelete() {
      this.showConfirmModal = false;
      this.sucursalToDelete = null;
    },

    async deleteSucursal(sucursal) {
      if (!this.showConfirmModal) {
        this.sucursalToDelete = sucursal;
        this.showConfirmModal = true;
        return;
      }

      this.isLoading = true;
      try {
        const parametros = `/sucursales/desactivar-sucursal/${this.sucursalToDelete.id_sucursal}`;
        const response = await solicitudes.desactivarRegistroAuth(parametros, {
          estado: false,
        });

        if (response === true) {
          this.sucursales = this.sucursales.filter(
            (item) => item.id_sucursal !== this.sucursalToDelete.id_sucursal
          );
          this.generateRows();
          notis("success", "Sucursal eliminada correctamente");
          this.showConfirmModal = false;
          this.sucursalToDelete = null;
        } else {
          throw new Error("Error al eliminar la sucursal");
        }
      } catch (error) {
        notis("error", "Error al eliminar la sucursal");
      } finally {
        this.isLoading = false;
      }
    },

    generateRows() {
      this.rows = this.paginatedSucursales.map((sucursal, index) => ({
        index: index + 1,
        nombre: sucursal.nombre_administrativo,
        direccion: sucursal.direccion,
        telefono: sucursal.telefono,
        correo: sucursal.correo,
      }));
    },

    async descargarDatosSucursal(sucursal) {
      if (this.descargandoSucursal === sucursal.id_sucursal) return;
      this.descargandoSucursal = sucursal.id_sucursal;
      try {
        const { getApi } = await import('../../config/getApiUrl.js');
        const token = localStorage.getItem('auth');
        const res = await fetch(`${getApi()}/empresa/exportar-datos-sucursal/${sucursal.id_sucursal}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          notis('error', body.error || 'Error al generar el archivo');
          return;
        }
        const cd       = res.headers.get('Content-Disposition') || '';
        const match    = cd.match(/filename="?([^"]+)"?/);
        const filename = match?.[1] ? decodeURIComponent(match[1]) : `${sucursal.nombre_administrativo}.zip`;
        const blob     = await res.blob();
        const url      = URL.createObjectURL(blob);
        const a        = document.createElement('a');
        a.href         = url;
        a.download     = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        notis('success', `Datos de "${sucursal.nombre_administrativo}" descargados`);
      } catch {
        notis('error', 'Error de conexión al generar el ZIP');
      } finally {
        this.descargandoSucursal = null;
      }
    },

    previousPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },

    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
      }
    },

    changeFavicon(iconPath) {
      const link =
        document.querySelector("link[rel*='icon']") ||
        document.createElement("link");
      link.type = "image/x-icon";
      link.rel = "icon";
      link.href = iconPath;
      document.getElementsByTagName("head")[0].appendChild(link);
    },
  },
  watch: {
    searchQuery() {
      this.currentPage = 1;
    },
    paginatedSucursales: {
      handler() {
        this.generateRows();
      },
      deep: true,
    },
  },
  async mounted() {
    this.isLoading = true;
    setPageTitle("Sucursales");
    try {
      this.id_usuario = await solicitudes.solicitarUsuarioToken();
      this.sucursales = await solicitudes.fetchRegistrosAuth(
        `/sucursales/empresa/${this.id_usuario}`
      );
      this.generateRows();
    } catch (error) {
      console.error(error);
    } finally {
      this.isLoading = false;
    }
    const _s = getSocket();
    if (_s) {
      this._wsRefresh = async () => { if (this.id_usuario) { this.sucursales = await solicitudes.fetchRegistrosAuth(`/sucursales/empresa/${this.id_usuario}`); this.generateRows(); } };
      _s.on('sucursales:refresh', this._wsRefresh);
    }
  },
  beforeUnmount() {
    const _s = getSocket();
    if (_s && this._wsRefresh) _s.off('sucursales:refresh', this._wsRefresh);
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,100..900;1,100..900&display=swap");

* {
 font-family: "Montserrat", sans-serif;
 box-sizing: border-box;
}

.opciones {
 display: flex;
 align-items: center;
 justify-content: flex-start;
 flex-wrap: wrap;
 gap: 16px;
 margin-bottom: 16px;
 width: 100%;
}

.busqueda {
 padding: 10px;
 font-size: 14px;
 border-radius: 10px;
 border: 1px solid #ddd;
 width: 100%;
 max-width: 300px;
 background-color: white;
 color: #333;
}

.search-bar {
 flex: 1;
 min-width: 200px;
}

input:focus,
select:focus,
textarea:focus {
 outline: none;
 border-color: #c09d62;
 box-shadow: 0 0 0 3px rgba(192, 157, 98, 0.2);
 transition: all 0.3s ease;
}

.registros {
 height: 100%;
 padding-bottom: 1%;
 flex-grow: 1;
 min-width: 200px;
}

#btnAdd {
 background-color: #c09d62;
 font-size: 16px;
 width: 170px;
 height: 40px;
 border-radius: 10px;
 color: black;
 font-weight: bold;
 transition: all 0.3s ease;
 white-space: nowrap;
}

#btnAdd:hover {
 background-color: #a38655;
 transform: scale(1.05);
}

.export-button {
 margin: 0;
}

.h2-modal-content {
 margin-top: 0;
 font-size: clamp(1.2rem, 4vw, 1.5rem);
}

#btnEditar,
#btnEliminar {
 font-size: 18px;
 width: 50px;
 height: 40px;
 border-radius: 10px;
}

#btnEditar:hover {
 background-color: #e8af06;
 transform: scale(1.05);
 transition: all 0.3s ease;
}

#btnEliminar {
 color: black;
}

#btnEliminar:hover {
 background-color: #b72433;
 transform: scale(1.05);
 transition: all 0.3s ease;
}

#campana {
 margin-right: 10px;
 font-size: 18px;
 color: #a38655;
}

.container-top {
 width: 100%;
 text-align: right;
}

.rol {
 color: #969696;
 font-size: 14px;
}

.col-id {
 width: 5%;
}

.col-nombre {
 width: 10%;
}

.col-correo {
 width: 10%;
}

.col-telefono {
 width: 5%;
}

.col-direccion {
 width: 20%;
}

.col-acciones {
 width: 15%;
}

.custom-select {
 border: 1px solid #ccc;
 border-radius: 5px;
 margin: 10px 5px 0 5px;
 border-radius: 5px;
 height: 35px;
 font-size: 16px;
 padding: 5px;
 background-color: #fff;
 cursor: pointer;
 width: 80px;
}

.sucursales-wrapper {
 padding: 16px;
 width: 100%;
 overflow-x: hidden;
 background-color: #f8f9fa;
}

.table-container {
 width: 100%;
 border-radius: 8px;
 border: 1px solid #e2e8f0;
 margin-top: 16px;
 background-color: white;
 box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
 overflow: hidden;
}

.table {
 width: 100%;
 min-width: 800px;
 border-collapse: separate;
 border-spacing: 0;
 background-color: white;
}

.table thead {
 position: sticky;
 top: 0;
 z-index: 1;
 background-color: #f8f9fa;
}

.table th,
.table td {
 padding: 12px 8px;
 text-align: center;
 color: #333;
}

.table thead th {
 border-bottom: 1px solid #dee2e6;
 background-color: #f8f9fa;
 font-weight: 600;
 color: #495057;
}

.table tbody td {
 border-bottom: 1px solid #f1f3f4;
 background-color: white;
}

.table tbody tr:hover {
 background-color: #f8f9fa;
}

.table tbody tr:hover td {
 background-color: #f8f9fa;
}

.table thead th:first-child {
 border-top-left-radius: 8px;
}

.table thead th:last-child {
 border-top-right-radius: 8px;
}

.table tbody tr:last-child td:first-child {
 border-bottom-left-radius: 8px;
}

.table tbody tr:last-child td:last-child {
 border-bottom-right-radius: 8px;
}

.btn {
 padding: 8px 16px;
 margin: 4px;
 border: none;
 cursor: pointer;
}

#AddSucursalModal,
#BtnCerrar {
 padding: 0.75rem 1.5rem;
 border: none;
 border-radius: 4px;
 font-size: 1rem;
 color: #fff;
 cursor: pointer;
 margin-right: 1rem;
}

#AddSucursalModal {
 background-color: #007bff;
}

#BtnCerrar {
 background-color: rgb(93, 100, 104);
}

/* Botón header */
.btn-agregar {
  background-color: #c09d62;
  color: #1a1a1a;
  padding: 8px 16px;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.3s ease;
}
.btn-agregar:hover { background-color: #a38655; }

/* Botones de fila compactos */
.accion-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  font-size: 14px;
  color: #fff;
  transition: opacity 0.2s, transform 0.15s;
  margin: 2px;
}
.accion-btn:hover { opacity: 0.85; transform: scale(1.08); }
.accion-editar    { background-color: #f59e0b; color: #1a1a1a; }
.accion-descargar { background-color: #3b82f6; color: #fff; }
.accion-descargar:disabled { opacity: 0.55; cursor: not-allowed; transform: none !important; }
.accion-eliminar  { background-color: #ef4444; }

@keyframes spin { to { transform: rotate(360deg); } }
.spin { display: inline-block; animation: spin 0.8s linear infinite; }

.modal {
 position: fixed;
 top: 0;
 left: 0;
 width: 100%;
 height: 100%;
 background-color: rgba(0, 0, 0, 0.5);
 display: flex;
 justify-content: center;
 align-items: center;
 z-index: 1000;
 padding: 1rem;
}

.modal-sucursales {
 background: white;
 border-radius: 12px;
 width: 35%;
 max-width: 1000px;
 max-height: 90vh;
 padding: 0;
 display: flex;
 flex-direction: column;
 box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
 margin: 20px;
 overflow: hidden;
}

.modal-confirm {
 background: white;
 border-radius: 12px;
 width: 35%;
 max-width: 400px;
 min-width: 300px;
 padding: 0;
 display: flex;
 flex-direction: column;
 box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
 margin: 20px;
}

.modal-header {
 padding: 24px;
 border-bottom: 1px solid #e0e0e0;
 background-color: #f8f9fa;
}

.modal-header h2 {
 margin: 0;
 color: #333;
 font-size: 1.5rem;
 font-weight: 600;
}

.modal-body {
 padding: 24px;
 overflow-y: auto;
 background-color: white;
}

/* Form Layout */
.form-row {
 margin-bottom: 20px;
}

.form-columns {
 display: grid;
 grid-template-columns: repeat(2, 1fr);
 gap: 24px;
 margin-bottom: 20px;
}

.full-width {
 grid-column: 1 / -1;
}

/* Form Groups */
.form-group {
 margin-bottom: 20px;
}

.form-group label {
 display: block;
 margin-bottom: 8px;
 color: #333;
 font-weight: 500;
}

.form-group input,
.form-group textarea,
.form-group select {
 width: 100%;
 padding: 10px 12px;
 border: 1px solid #ddd;
 border-radius: 8px;
 font-size: 14px;
 transition: all 0.3s ease;
 background-color: white;
 color: #333;
}

.form-group input:focus,
.form-group textarea:focus,
.form-group select:focus {
 outline: none;
 border-color: #c09d62;
 box-shadow: 0 0 0 3px rgba(192, 157, 98, 0.2);
}

.form-group textarea {
 min-height: 100px;
 resize: vertical;
}

/* Footer */
.modal-footer {
 padding: 20px 24px;
 border-top: 1px solid #e0e0e0;
 display: flex;
 justify-content: space-between;
 align-items: center;
 background-color: #f8f9fa;
}

.action-buttons {
 display: flex;
 gap: 12px;
}

/* Custom scrollbar */
.table-container::-webkit-scrollbar {
 width: 8px;
 height: 8px;
}

.table-container::-webkit-scrollbar-track {
 background: #f1f1f1;
 border-radius: 4px;
}

.table-container::-webkit-scrollbar-thumb {
 background: #c09d62;
 border-radius: 4px;
}

.table-container::-webkit-scrollbar-thumb:hover {
 background: #a38655;
}

/* Large Desktop (1440px y más) */
@media screen and (min-width: 1440px) {
 .table-container {
   margin: 20px auto;
 }

 .modal-sucursales {
   width: 30%;
   min-width: 500px;
 }

 .form-group input,
 .form-group select {
   padding: 12px 16px;
   font-size: 1rem;
 }
}

/* Desktop (1200px a 1439px) */
@media screen and (max-width: 1200px) {
 .modal-sucursales {
   width: 40%;
   min-width: 450px;
 }

 .table {
   min-width: 900px;
 }

 .opciones {
   gap: 12px;
 }
}

/* Tablet Landscape (992px a 1199px) */
@media screen and (max-width: 992px) {
 .modal-sucursales {
   width: 50%;
   min-width: 400px;
 }

 .opciones {
   flex-wrap: wrap;
   gap: 10px;
 }

 #btnAdd {
   width: auto;
   min-width: 200px;
 }

 .busqueda {
   width: 250px;
 }

 .table th,
 .table td {
   padding: 10px 8px;
   font-size: 0.9rem;
 }
}

/* Tablet Portrait (768px a 991px) */
@media screen and (max-width: 768px) {
 .sucursales-wrapper {
   padding: 12px;
 }

 .modal-sucursales {
   width: 70%;
   margin: 10px;
 }

 .opciones {
   display: grid;
   grid-template-columns: 1fr 1fr;
   grid-template-areas:
     "add export"
     "search search";
   gap: 12px;
 }

 .search-bar {
   grid-area: search;
   width: 100%;
 }

 .busqueda {
   max-width: 100%;
   width: 100%;
 }

 #btnAdd {
   grid-area: add;
   width: 100%;
   white-space: nowrap;
 }

 .table-container {
   margin-top: 16px;
   overflow-x: auto;
 }

 .table {
   min-width: 700px;
 }

 .form-group {
   margin-bottom: 16px;
 }

 .phone-input-container {
   flex-direction: column;
   gap: 8px;
 }

 .phone-input-container select,
 .phone-input-container input {
   width: 100%;
 }

 .pagination-wrapper {
   flex-direction: column;
   gap: 12px;
   align-items: center;
 }
 .export-button {
   grid-area: export;
   width: 100%;
 }
}

/* Mobile (576px a 767px) */
@media screen and (max-width: 576px) {
 .sucursales-wrapper {
   padding: 8px;
 }

 .modal-sucursales {
   width: 90%;
   margin: 8px;
 }

 .modal-header {
   padding: 16px;
 }

 .modal-body {
   padding: 16px;
 }

 .h2-modal-content {
   font-size: 1.2rem;
 }

 .form-group label {
   font-size: 0.9rem;
 }

 .form-group input,
 .form-group select {
   padding: 8px 12px;
   font-size: 0.9rem;
 }

 #btnEditar,
 #btnEliminar {
   width: 40px;
   height: 35px;
   font-size: 14px;
 }

 .pagination-button {
   padding: 6px 12px;
   font-size: 0.9rem;
 }

 .opciones {
   grid-template-columns: 1fr;
   grid-template-areas:
     "add"
     "export"
     "search";
   gap: 10px;
 }

 #btnAdd,
 .export-button,
 .search-bar,
 .busqueda {
   width: 100%;
 }
}

/* Small Mobile (400px y menos) */
@media screen and (max-width: 400px) {
 .sucursales-wrapper {
   padding: 6px;
 }

 .modal-sucursales {
   width: 95%;
   margin: 6px;
 }

 .h2-modal-content {
   font-size: 1.1rem;
 }

 .modal-header,
 .modal-body,
 .modal-footer {
   padding: 12px;
 }

 .form-group {
   margin-bottom: 12px;
 }

 .form-group label {
   font-size: 0.875rem;
   margin-bottom: 4px;
 }

 .form-group input,
 .form-group select {
   padding: 6px 10px;
   font-size: 0.875rem;
 }

 .pagination-info {
   font-size: 0.875rem;
 }

 .pagination-button {
   padding: 5px 10px;
   font-size: 0.875rem;
 }

 #btnEditar,
 #btnEliminar {
   width: 35px;
   height: 32px;
   font-size: 12px;
 }

 .opciones {
   gap: 8px;
   margin-bottom: 12px;
 }

 #btnAdd {
   height: 35px;
   font-size: 14px;
 }
}

.phone-input-container {
 display: flex;
 gap: 8px;
}

.phone-input-container select {
 width: 110px;
 margin: 0;
 height: 35px;
 padding: 0 8px;
 border: 1px solid #ddd;
 border-radius: 4px;
 background-color: white;
 color: #333;
}

.phone-input-container input {
 flex: 1;
 height: 35px;
 padding: 0 12px;
 border: 1px solid #ddd;
 border-radius: 4px;
 background-color: white;
 color: #333;
}

/* Nueva paginación */
.loading-indicator,
.no-data {
 text-align: center;
 padding: 2rem;
 font-size: 1.1rem;
 color: #666;
}

.no-data {
 background-color: #f8f9fa;
 border-radius: 4px;
}

.pagination-wrapper {
 display: flex;
 justify-content: space-between;
 align-items: center;
 padding: 1rem;
 border-top: 1px solid #dee2e6;
 margin-top: auto;
 background-color: white;
}

.pagination-info {
 color: #6c757d;
}

.pagination-container {
 display: flex;
 gap: 5px;
}

.pagination-button {
 padding: 8px 16px;
 border: 1px solid #dee2e6;
 background-color: white;
 cursor: pointer;
 border-radius: 4px;
 min-width: 40px;
 transition: all 0.3s ease;
 color: #333;
}

.pagination-button:hover:not(:disabled) {
 background-color: #e9ecef;
}

.pagination-button:disabled {
 cursor: not-allowed;
 opacity: 0.6;
}

/* SOLO aplicar modo oscuro cuando la clase .dark esté presente */
.dark .sucursales-wrapper {
 background-color: #1e1e1e;
 color: #fff;
}

.dark .busqueda {
 background-color: #2d2d2d;
 border-color: #404040;
 color: #fff;
}

.dark .custom-select {
 background-color: #2d2d2d;
 border-color: #404040;
 color: #fff;
}

.dark .custom-select option {
 background-color: #2d2d2d;
 color: #fff;
}

.dark .table-container {
 background-color: #1e1e1e;
 border-color: #374151;
}

.dark .table {
 background-color: #1e1e1e;
}

.dark .table thead {
 background-color: #111827;
}

.dark .table th {
 background-color: #111827;
 color: #e5e7eb;
 border-bottom-color: #374151;
}

.dark .table td {
 color: #e5e7eb;
 border-bottom-color: #374151;
 background-color: #1e1e1e;
}

.dark .table tbody tr:hover {
 background-color: #1f2937;
}

.dark .table tbody tr:hover td {
 background-color: #1f2937;
}

.dark .modal-sucursales,
.dark .modal-confirm {
 background-color: #2d2d2d;
}

.dark .modal-header,
.dark .modal-footer {
 background-color: #1e1e1e;
 border-color: #404040;
}

.dark .modal-header h2 {
 color: #fff;
}

.dark .modal-body {
 background-color: #2d2d2d;
}

.dark .form-group label {
 color: #fff;
}

.dark .form-group input,
.dark .form-group textarea,
.dark .form-group select {
 background-color: #383838;
 border-color: #404040;
 color: #fff;
}

.dark .phone-input-container select,
.dark .phone-input-container input {
 background-color: #383838;
 border-color: #404040;
 color: #fff;
}

.dark .table-container::-webkit-scrollbar-track {
 background: #2d2d2d;
}

.dark .table-container::-webkit-scrollbar-thumb {
 background: #c09d62;
}

.dark .table-container::-webkit-scrollbar-thumb:hover {
 background: #a38655;
}

.dark #btnAdd {
 background-color: #c09d62;
 color: black;
}

.dark #btnAdd:hover {
 background-color: #a38655;
}

.dark #btnEditar {
 background-color: #ffc107;
 color: black;
}

.dark #btnEditar:hover {
 background-color: #e8af06;
}

.dark #btnEliminar {
 background-color: #dc3545;
 color: black;
}

.dark #btnEliminar:hover {
 background-color: #b72433;
}

.dark .pagination-wrapper {
 border-color: #404040;
 background-color: #2d2d2d;
}

.dark .pagination-info {
 color: #aaa;
}

.dark .pagination-button {
 background-color: #2d2d2d;
 border-color: #404040;
 color: #fff;
}

.dark .pagination-button:hover:not(:disabled) {
 background-color: #383838;
}

.dark .loading-indicator,
.dark .no-data {
 color: #aaa;
 background-color: #2d2d2d;
}
</style>